/**
 * DurationSheet - Mobile bottom sheet for picking the booking duration
 * Opened from the header's duration pill; commits on tap, "Done" only dismisses
 */

import { BottomSheet } from '@butter/ui/patterns';
import { formatDuration } from './utils';

export interface DurationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  durationOptions: number[];
  selectedDuration: number;
  /** Durations the backend can't serve from the current start time. */
  disabledDurations?: number[];
  onSelect: (minutes: number) => void;
  formatOptionLabel?: (minutes: number) => string;
}

// One description, because there is only one way in: the header pill. There used to be a
// second — an 'extend' variant whose copy read "If you'd like to extend your booking,
// choose a longer booking length" — set only by a tap on the slot at the selection's end.
// That gesture is gone, and the copy was never accurate anyway: the chips below have
// always let you pick a SHORTER length, which shrinks the booking.
const DESCRIPTION = "Choose how long you'd like to book for";

export function DurationSheet({
  open,
  onOpenChange,
  durationOptions,
  selectedDuration,
  disabledDurations,
  onSelect,
  formatOptionLabel = formatDuration,
}: DurationSheetProps) {
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Booking length"
      description={DESCRIPTION}
      footer={
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="w-full rounded-xl bg-theme-600 px-4 py-3 text-sm font-semibold text-white transition-colors text-interactive:bg-theme-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2"
          data-testid="booking-duration-sheet-done"
        >
          Done
        </button>
      }
    >
      <div role="group" aria-label="Booking length options" className="grid grid-cols-3 gap-2">
        {durationOptions.map((minutes) => {
          const isDisabled = disabledDurations?.includes(minutes) ?? false;
          return (
            <button
              key={minutes}
              type="button"
              aria-pressed={minutes === selectedDuration}
              disabled={isDisabled}
              aria-disabled={isDisabled}
              // A `disabled` button is out of the tab order, so a screen-reader user
              // browsing the sheet would otherwise hear "2 hr, dimmed" with no way to
              // reach it and find out why. `StyleAndA11y.mdx` pairs the two attributes
              // with a label that carries the reason.
              aria-label={isDisabled ? `${formatDuration(minutes)}, unavailable from this start time` : undefined}
              data-testid={`booking-duration-option-${minutes}`}
              onClick={() => onSelect(minutes)}
              // `hover:`, NOT `text-interactive:`, and deliberately against CLAUDE.md's general
              // preference. That variant is `hover` + `&:focus`, and BottomSheet is a Radix
              // Dialog which auto-focuses its first tabbable child on open — this chip. Its
              // focus styling is the SAME two tokens as `aria-pressed`, so opening the sheet
              // painted "30 min" as a second selected option next to the real one. Focus is
              // shown by the ring below, which `:focus-visible` correctly withholds from a
              // programmatic focus.
              className="min-h-11 rounded-full border border-theme-100 bg-bg-tertiary px-2 py-2 text-sm font-semibold text-fg-tertiary hover:border-theme-600 hover:bg-theme-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2 aria-pressed:border-theme-600 aria-pressed:bg-theme-50 aria-pressed:text-theme-600 aria-disabled:cursor-not-allowed aria-disabled:border-theme-100 aria-disabled:opacity-50"
            >
              {formatOptionLabel(minutes)}
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}
